/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext} from '@msdyn365-commerce/core';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    getPayloadObject,
    getTelemetryAttributes,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import { ICartIconData } from '../../../Utilities/cart-utils';


export interface IIncrementalQuantityProps {
    min?: number;
    max: number;
    currentCount?: number;
    disabled?: boolean;
    incrementGlyphClass?: string;
    decrementGlyphClass?: string;
    decrementButtonAriaLabel?: string;
    incrementButtonAriaLabel?: string;
    minQuantityText: string;
    maxQuantityText: string;
    inputQuantityAriaLabel?: string;
    isGiftCard?: boolean;
    id?: string;
    telemetryContent?: ITelemetryContent;
    context: ICoreContext;
    bonusItem?: boolean;
    onChange?(newValue: number): Promise<boolean>;
    onDelete?(): void;
    productDeleteIcon: ICartIconData;
    productIncrementIcon: ICartIconData;
    productDecrementIcon: ICartIconData;
}

/**
 * IIncrementalQuantityState interface.
 */
interface IIncrementalQuantityState {
    currentInput: number;
    isUpdating: boolean;
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class IncrementalQuantity extends React.PureComponent<IIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityProps> = {
        min: 1,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private readonly _minValue: number;

    private readonly payLoad: IPayLoad;

    public static getDerivedStateFromProps(
        props: Readonly<IIncrementalQuantityProps>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        if (!props.disabled && state.isUpdating) {
            nextState.currentInput = props.currentCount ?? 1;
            nextState.isUpdating = false;
        }
        return nextState;
    }

    constructor(props: IIncrementalQuantityProps) {
        super(props);
        this.state = { currentInput: props.currentCount ?? 1, isUpdating: false };
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
        this._minValue = 1;
    }

    public render(): JSX.Element {
        const { min, max } = this.props;
        const decrementDisabled = this.state.currentInput <= (min ?? 1) || this.props.isGiftCard;
        const incrementDisabled = this.state.currentInput > max || this.props.isGiftCard;
        this.payLoad.contentAction.etext = TelemetryConstant.DecrementQuantity;
        const decrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.IncrementQuantity;
        const incrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        const { productDeleteIcon, productDecrementIcon, productIncrementIcon } = this.props;

        return (
                <div className='msc-cart-line__quantity-container' id={this.props.id}>
                    <button
                        disabled={this.props.disabled || incrementDisabled}
                        tabIndex={0}
                        title={this.props.decrementButtonAriaLabel}
                        className={classNames('msc-cart-line__quantity-button', this.props.disabled && 'msc-cart-line__quantity-disabled')}
                        onClick={this._onDecrement}
                        aria-label={`${
                            this.props.bonusItem || decrementDisabled
                                ? productDeleteIcon?.ariaLabel
                                : productDecrementIcon?.ariaLabel
                        }`}
                        {...decrementAttributes}
                    >
                        <img
                            tabIndex={-1}
                            className='msc-cart-line__quantity-icon'
                            src={`${
                                this.props.bonusItem || decrementDisabled
                                    ? productDeleteIcon?.iconUrl
                                    : productDecrementIcon?.iconUrl
                            }`}
                            alt={`${
                                this.props.bonusItem || decrementDisabled
                                    ? productDeleteIcon?.altText
                                    : productDecrementIcon?.altText
                            }`}
                        />
                    </button>
                    <p className='msc-cart-line__quantity-text' tabIndex={0}>
                        <span tabIndex={-1}>{this.state.currentInput}</span>
                    </p>
                    <button
                        disabled={this.props.disabled || incrementDisabled || this.props.bonusItem}
                        title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                        className={classNames('msc-cart-line__quantity-button', this.props.disabled || this.props.bonusItem && 'msc-cart-line__quantity-disabled')}
                        onClick={this._onIncrement}
                        aria-hidden={!!incrementDisabled}
                        aria-label={`${productIncrementIcon?.ariaLabel}`}
                        {...incrementAttributes}
                    >
                        <img
                            tabIndex={-1}
                            className='msc-cart-line__quantity-icon'
                            src={productIncrementIcon?.iconUrl}
                            alt={productIncrementIcon?.altText}
                        />
                    </button>
                </div>
        );
    }

    private _onIncrement = debounce(async () => {
        // Flag to determine if callback should be invoked
        let invokeCallback = false;

        // Get the current quantity value
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        // Check if current quantity is less than the maximum allowed value
        if (currQuantity < this.props.max) {
            invokeCallback = true;
            updatedQuantity = currQuantity + 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this.props.max;
        }

        // Invoke the callback if required and handle the response
        if (invokeCallback && this.props.onChange) {
            if (await this.props.onChange(updatedQuantity)) {
                // Update the state with the updated quantity
                this.setState({ currentInput: updatedQuantity });
            }
        }
    }, 1000);

    private _onDecrement = debounce(async () => {
        // Flag to determine if callback should be invoked
        let invokeCallback = false;

        // Get the current quantity value
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;
        // Check if current quantity is greater than the minimum allowed value
        if (currQuantity > this._minValue) {
            invokeCallback = true;
            updatedQuantity = currQuantity - 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this._minValue;
        }
        // Invoke the callback if required and handle the response
        if (invokeCallback && this.props.onChange && !this.props.bonusItem) {
            if (await this.props.onChange(updatedQuantity)) {
                // Update the state with the updated quantity
                this.setState({ currentInput: updatedQuantity });
            }
        } else {
            // If the callback is not invoked, check if onDelete prop is provided and call it
            if (this.props.onDelete) {
                this.props.onDelete();
            }
        }
    }, 1000);

}
